import { useState } from "react";
import PageHeader from "@/components/base/PageHeader";
import Card from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Avatar from "@/components/base/Avatar";
import Modal from "@/components/base/Modal";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import Pagination from "@/components/base/Pagination";
import { Input, Select } from "@/components/base/Field";
import { LoadingState, ErrorState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import { useAsync } from "@/hooks/useAsync";
import { usePager } from "@/hooks/usePager";
import { useAuth } from "@/contexts/AuthContext";
import { getClasses } from "@/services/academicService";
import { getActiveSchoolYear } from "@/services/schoolService";
import {
  createGuardian,
  deleteGuardian,
  getEnrollments,
  getGuardians,
  getStudentGuardians,
  getStudents,
  linkGuardian,
  unlinkGuardian,
  updateGuardian,
  updateGuardianLink,
} from "@/services/studentService";
import { GUARDIAN_RELATION_LABEL, GUARDIAN_RELATIONS } from "@/utils/enrollment";
import type { Guardian, GuardianRelation, Student, StudentGuardian } from "@/types";
import { useUrlSearch } from "@/hooks/useUrlSearch";
import { plural } from "@/utils/format";

async function load() {
  const [guardians, links, students, enrollments, classes] = await Promise.all([
    getGuardians(),
    getStudentGuardians(),
    getStudents(),
    getEnrollments(),
    getClasses(),
  ]);
  return { guardians, links, students, enrollments, classes };
}

const EMPTY: Omit<Guardian, "id"> = { firstName: "", lastName: "", phone: "", address: "", profession: "" };

/**
 * Parents d'élèves : liste avec recherche, ajout, modification, suppression (si plus aucun enfant), et leurs
 * enfants dans l'école (classe de l'année consultée), avec ajout, modification ou retrait d'un lien.
 */
export default function GuardiansPage() {
  const { notify } = useToast();
  const { can } = useAuth();
  const schoolYear = getActiveSchoolYear();
  const { data, loading, error, reload } = useAsync(load, []);
  const pager = usePager();
  const [search, setSearch] = useUrlSearch();
  const [childFilter, setChildFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Guardian | null>(null);
  const [form, setForm] = useState<Omit<Guardian, "id">>(EMPTY);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<Guardian | null>(null);
  const [busy, setBusy] = useState(false);
  const [newLink, setNewLink] = useState<{ studentId: string; relation: GuardianRelation; isPrimary: boolean }>({
    studentId: "",
    relation: "PERE",
    isPrimary: false,
  });

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (loading && !data) return <LoadingState label="Chargement des parents d'élèves..." />;
  if (!data) return null;
  const { guardians, links, students, enrollments, classes } = data;

  const studentById = new Map(students.map((s) => [s.id, s]));
  const className = (studentId: string) => {
    const enrollment = enrollments.find(
      (e) => e.studentId === studentId && e.schoolYear === schoolYear && e.status !== "CANCELLED",
    );
    return enrollment ? classes.find((c) => c.id === enrollment.classId)?.name ?? "—" : `Non inscrit en ${schoolYear}`;
  };
  const childrenOf = (guardianId: string) =>
    links
      .filter((l) => l.guardianId === guardianId)
      .map((link) => ({ link, student: studentById.get(link.studentId) }))
      .filter((c): c is { link: StudentGuardian; student: Student } => !!c.student);

  const q = search.trim().toLowerCase();
  const filtered = guardians
    .filter((g) => {
      const count = childrenOf(g.id).length;
      if (childFilter === "with" && count === 0) return false;
      if (childFilter === "without" && count > 0) return false;
      if (!q) return true;
      return (
        `${g.firstName} ${g.lastName}`.toLowerCase().includes(q) ||
        g.phone.toLowerCase().includes(q) ||
        childrenOf(g.id).some(({ student }) => `${student.firstName} ${student.lastName}`.toLowerCase().includes(q))
      );
    })
    .sort((a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName));
  const page = pager.slice(filtered, `${q}|${childFilter}`);
  const detail = guardians.find((g) => g.id === detailId) ?? null;

  const act = async (action: () => Promise<unknown>, message: string) => {
    setBusy(true);
    try {
      await action();
      notify(message);
      await reload();
      return true;
    } catch (err) {
      notify(err instanceof Error ? err.message : "Action impossible.", "error");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const openForm = (guardian: Guardian | null) => {
    setEditing(guardian);
    setForm(guardian ? { ...guardian } : EMPTY);
    setFormOpen(true);
  };

  const save = async () => {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.phone.trim()) {
      notify("Nom, prénom et téléphone sont obligatoires.", "error");
      return;
    }
    const ok = await act(
      () => (editing ? updateGuardian(editing.id, form) : createGuardian(form)),
      editing ? "Parent modifié." : `${form.firstName} ${form.lastName} ajouté(e).`,
    );
    if (ok) setFormOpen(false);
  };

  const linkableStudents = detail
    ? students
        .filter((s) => !childrenOf(detail.id).some(({ student }) => student.id === s.id))
        .sort((a, b) => a.lastName.localeCompare(b.lastName))
    : [];

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Parents d'élèves"
        subtitle={`${plural(guardians.length, "parent ou responsable", "parents ou responsables")} — enfants affichés avec leur classe ${schoolYear}`}
        icon="ri-parent-line"
        actions={
          can("guardians:create") ? (
            <Button icon="ri-user-add-line" onClick={() => openForm(null)}>
              Ajouter un parent
            </Button>
          ) : undefined
        }
      />

      <Card padded={false}>
        <div className="flex flex-col gap-3 border-b border-background-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-sm font-semibold font-heading text-foreground-950">Parents ({filtered.length})</h3>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-1.5">
              <i className="ri-search-line text-foreground-400 text-sm"></i>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Parent, téléphone ou enfant..."
                className="w-52 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>
            <select
              value={childFilter}
              onChange={(e) => setChildFilter(e.target.value)}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm cursor-pointer focus:outline-none"
            >
              <option value="all">Tous les parents</option>
              <option value="with">Avec enfant(s)</option>
              <option value="without">Sans enfant rattaché</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="border-b border-background-200 text-left text-xs uppercase tracking-wide text-foreground-500">
                <th className="px-4 py-3 font-medium">Parent</th>
                <th className="px-4 py-3 font-medium">Contact</th>
                <th className="px-4 py-3 font-medium">Enfants dans l'école</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {page.items.map((g) => {
                const children = childrenOf(g.id);
                return (
                  <tr key={g.id} className="border-b border-background-200 last:border-0 hover:bg-background-100/60 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar lastName={g.lastName} firstName={g.firstName} size="sm" />
                        <div>
                          <p className="font-medium text-foreground-900">
                            {g.firstName} {g.lastName}
                          </p>
                          <p className="text-xs text-foreground-500">{g.profession || "—"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-foreground-800">{g.phone}</p>
                      <p className="text-xs text-foreground-500">{g.address || "—"}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex max-w-[360px] flex-wrap gap-1.5">
                        {children.map(({ link, student }) => (
                          <Badge key={student.id} tone={link.isPrimary ? "primary" : "neutral"}>
                            {student.firstName} {student.lastName} · {className(student.id)}
                          </Badge>
                        ))}
                        {children.length === 0 && <span className="text-xs text-foreground-400">Aucun enfant rattaché</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setDetailId(g.id)}
                          className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer"
                          aria-label="Voir ses enfants"
                          title="Voir ses enfants"
                        >
                          <i className="ri-eye-line"></i>
                        </button>
                        {can("guardians:update") && (
                          <button
                            onClick={() => openForm(g)}
                            className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer"
                            aria-label="Modifier"
                            title="Modifier"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                        )}
                        {can("guardians:delete") && (
                          <button
                            onClick={() => setToDelete(g)}
                            disabled={children.length > 0}
                            className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-accent-50 hover:text-accent-600 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                            aria-label="Supprimer"
                            title={children.length > 0 ? "Retirez d'abord ses enfants (ou rattachez-les à un autre parent)" : "Supprimer"}
                          >
                            <i className="ri-delete-bin-6-line"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-sm text-foreground-400">
                    Aucun parent pour cette recherche.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination pager={page} className="border-t border-background-200" />
      </Card>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? "Modifier le parent" : "Ajouter un parent"}
        icon="ri-parent-line"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              Annuler
            </Button>
            <Button icon="ri-save-3-line" onClick={save} disabled={busy}>
              Enregistrer
            </Button>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="Prénom" required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
          <Input label="Nom" required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
          <Input label="Téléphone" required placeholder="+224 6.. .. .. .." value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Input label="Profession" value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} />
          <Input label="Adresse" wrapperClass="sm:col-span-2" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </div>
      </Modal>

      <Modal
        open={detail !== null}
        onClose={() => setDetailId(null)}
        title={detail ? `${detail.firstName} ${detail.lastName}` : ""}
        subtitle={detail ? `${detail.phone}${detail.profession ? ` • ${detail.profession}` : ""}` : undefined}
        icon="ri-parent-line"
        size="lg"
      >
        {detail && (
          <div className="space-y-5">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground-400">
                Enfants dans l'école ({childrenOf(detail.id).length})
              </p>
              <div className="space-y-2">
                {childrenOf(detail.id).map(({ link, student }) => (
                  <div key={student.id} className="flex flex-wrap items-center gap-3 rounded-md border border-background-200 px-3.5 py-2.5">
                    <Avatar lastName={student.lastName} firstName={student.firstName} src={student.photo || undefined} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground-900">
                        {student.firstName} {student.lastName}
                      </p>
                      <p className="text-xs text-foreground-500">
                        {student.matricule} • {className(student.id)}
                      </p>
                    </div>
                    {can("guardians:update") ? (
                      <>
                        <select
                          value={link.relation}
                          onChange={(e) =>
                            act(
                              () => updateGuardianLink({ ...link, relation: e.target.value as GuardianRelation }),
                              "Lien de parenté modifié.",
                            )
                          }
                          className="rounded-md border border-background-300 bg-background-50 px-2 py-1 text-xs cursor-pointer focus:outline-none"
                          aria-label="Lien de parenté"
                        >
                          {GUARDIAN_RELATIONS.map((r) => (
                            <option key={r} value={r}>
                              {GUARDIAN_RELATION_LABEL[r]}
                            </option>
                          ))}
                        </select>
                        {link.isPrimary ? (
                          <Badge tone="primary">Principal</Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => act(() => updateGuardianLink({ ...link, isPrimary: true }), "Responsable principal mis à jour.")}
                          >
                            Rendre principal
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          icon="ri-link-unlink"
                          disabled={busy}
                          onClick={() =>
                            act(
                              () => unlinkGuardian(student.id, detail.id),
                              `${student.firstName} n'est plus rattaché(e) à ce parent.`,
                            )
                          }
                        >
                          Retirer
                        </Button>
                      </>
                    ) : (
                      <Badge tone={link.isPrimary ? "primary" : "neutral"}>
                        {link.isPrimary ? "Principal" : GUARDIAN_RELATION_LABEL[link.relation]}
                      </Badge>
                    )}
                  </div>
                ))}
                {childrenOf(detail.id).length === 0 && (
                  <p className="rounded-md border border-dashed border-background-300 py-4 text-center text-xs text-foreground-400">
                    Aucun enfant rattaché à ce parent.
                  </p>
                )}
              </div>
            </div>

            {can("guardians:update") && (
              <div className="rounded-md border border-background-200 bg-background-100/50 p-3.5">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground-400">Rattacher un enfant</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_160px_auto]">
                  <Select label="Élève" value={newLink.studentId} onChange={(e) => setNewLink({ ...newLink, studentId: e.target.value })}>
                    <option value="">— Choisir un élève —</option>
                    {linkableStudents.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.lastName} {s.firstName} ({s.matricule})
                      </option>
                    ))}
                  </Select>
                  <Select
                    label="Lien"
                    value={newLink.relation}
                    onChange={(e) => setNewLink({ ...newLink, relation: e.target.value as GuardianRelation })}
                  >
                    {GUARDIAN_RELATIONS.map((r) => (
                      <option key={r} value={r}>
                        {GUARDIAN_RELATION_LABEL[r]}
                      </option>
                    ))}
                  </Select>
                  <div className="flex items-end">
                    <Button
                      icon="ri-link"
                      disabled={!newLink.studentId || busy}
                      onClick={async () => {
                        const ok = await act(
                          () =>
                            linkGuardian({
                              studentId: newLink.studentId,
                              guardianId: detail.id,
                              relation: newLink.relation,
                              isPrimary: newLink.isPrimary,
                            }),
                          "Enfant rattaché.",
                        );
                        if (ok) setNewLink({ studentId: "", relation: "PERE", isPrimary: false });
                      }}
                    >
                      Rattacher
                    </Button>
                  </div>
                </div>
                <label className="mt-2 flex items-center gap-2 text-xs text-foreground-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newLink.isPrimary}
                    onChange={(e) => setNewLink({ ...newLink, isPrimary: e.target.checked })}
                    className="h-3.5 w-3.5 accent-primary-500"
                  />
                  Responsable principal de cet enfant
                </label>
              </div>
            )}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={toDelete !== null}
        title="Supprimer le parent"
        message={`Supprimer ${toDelete?.firstName ?? ""} ${toDelete?.lastName ?? ""} ? Possible uniquement s'il n'a plus aucun enfant rattaché.`}
        confirmLabel="Supprimer"
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          const guardian = toDelete;
          if (guardian) void act(() => deleteGuardian(guardian.id), "Parent supprimé.");
        }}
      />
    </div>
  );
}
